/* eslint-disable @typescript-eslint/no-empty-function */
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useRef,
  useState,
} from 'react';

import { PaymentStep } from './constants';

export type StepContextType = {
  currentStep: PaymentStep;
  history: PaymentStep[];
  goToStep: (step: PaymentStep) => void;
  goBack: VoidFunction;
  canGoBack: boolean;
  registerStepCleanup: (
    step: PaymentStep,
    cleanup: VoidFunction,
  ) => VoidFunction;
};

export const StepContext = createContext<StepContextType>({
  currentStep: PaymentStep.SELECT_CERTIFICATE,
  history: [],
  goToStep() {},
  goBack() {},
  canGoBack: false,
  registerStepCleanup: () => () => {},
});

export const PaymentStepProvider = ({ children }: PropsWithChildren) => {
  const [history, setHistory] = useState<PaymentStep[]>([
    PaymentStep.SELECT_CERTIFICATE,
  ]);
  const cleanupRef = useRef<Partial<Record<PaymentStep, VoidFunction>>>({});

  const currentStep = history[history.length - 1];
  const canGoBack = history.length > 1;

  const goToStep = useCallback((step: PaymentStep) => {
    setHistory((prev) => [...prev, step]);
  }, []);

  const goBack = useCallback(() => {
    setHistory((prev) => {
      if (prev.length > 1) {
        const fromStep = prev[prev.length - 1];
        cleanupRef.current[fromStep]?.();
        return prev.slice(0, -1);
      }
      return prev;
    });
  }, []);

  const registerStepCleanup = useCallback(
    (step: PaymentStep, cleanup: VoidFunction) => {
      cleanupRef.current[step] = cleanup;

      return () => {
        cleanupRef.current[step] = undefined;
      };
    },
    [],
  );

  return (
    <StepContext.Provider
      value={{
        currentStep,
        history,
        goToStep,
        goBack,
        canGoBack,
        registerStepCleanup,
      }}
    >
      {children}
    </StepContext.Provider>
  );
};
